import React, { useState, useEffect, useCallback, useRef } from "react";
import BrandLogo from "./BrandLogo";
import { Link, matchPath } from "react-router-dom";
import { NavbarLinks } from "../../data/navbar-links";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { TiShoppingCart } from "react-icons/ti";
import ProfileDropDown from "../core/Auth/ProfileDropDown";
import { categories } from "../../services/apis";
import { apiConnector } from "../../services/apiConnector";
import { useDispatch } from "react-redux";
import { GiHamburgerMenu } from "react-icons/gi";
import { HiSearch } from "react-icons/hi";
import { useNavigate } from "react-router";

/** Dark bar + light text on every route (original StudyNotion-style chrome). */
const NavBar = ({ setProgress }) => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { user } = useSelector((state) => state.profile);
  const { totalItems } = useSelector((state) => state.cart);
  const [searchValue, setSearchValue] = useState("");
  const [catalogMenuOpen, setCatalogMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setCatalogMenuOpen(false);
  }, [location.pathname]);

  const matchRoutes = (routes) => {
    return matchPath({ path: routes }, location.pathname);
  };

  const readCachedSublinks = () => {
    try {
      const raw = localStorage.getItem("sublinks");
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const [sublinks, setsublinks] = useState(() => readCachedSublinks());
  const fetchSublinks = useCallback(async () => {
    try {
      const result = await apiConnector("GET", categories.CATEGORIES_API);
      const list = result?.data?.data;
      const next = Array.isArray(list) ? list : [];
      setsublinks(next);
      localStorage.setItem("sublinks", JSON.stringify(next));
    } catch (error) {
      const cached = readCachedSublinks();
      if (cached.length) {
        setsublinks(cached);
      }
      console.error("Could not fetch categories for catalog menu", error);
    }
  }, []);
  useEffect(() => {
    fetchSublinks();
  }, [fetchSublinks]);

  const menuRef = useRef();
  const overlay = useRef();

  const shownav = () => {
    menuRef.current.classList.toggle("navshow");
    overlay.current.classList.toggle("hidden");
  };

  const handelSearch = (e) => {
    e.preventDefault();
    if (searchValue?.length > 0) {
      navigate(`/search/${searchValue}`);
      setSearchValue("");
    }
  };

  const linkIdle = "text-richblack-25 hover:text-richblack-5";
  const linkActive =
    "font-semibold text-yellow-50 underline decoration-yellow-50 decoration-2 underline-offset-4";
  const iconFill = "fill-richblack-25";
  const cartBadge = "bg-yellow-50 text-richblack-900";

  return (
    <div className="sticky top-0 z-50 flex h-16 w-full shrink-0 items-center justify-center border-b border-richblack-700 bg-richblack-900 font-inter text-richblack-25 shadow-[0_1px_0_0_rgba(255,255,255,0.06)]">
      <div className="flex w-11/12 max-w-maxContent items-center justify-between gap-3">
        <Link
          to="/"
          onClick={() => dispatch(setProgress(100))}
          className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-50 focus-visible:ring-offset-2 focus-visible:ring-offset-richblack-900"
        >
          <BrandLogo asLink={false} className="text-richblack-25" />
        </Link>

        {user && user?.accountType !== "Instructor" && (
          <div className="md:hidden">
            <Link
              to="/dashboard/cart"
              className="relative inline-flex"
              onClick={() => dispatch(setProgress(100))}
            >
              <TiShoppingCart className={`${iconFill} h-7 w-7 hover:fill-richblack-5`} />
              {totalItems > 0 && (
                <span
                  className={`absolute -right-1 -top-1 rounded-full px-1 text-[11px] font-semibold leading-none shadow-md ${cartBadge}`}
                >
                  {totalItems}
                </span>
              )}
            </Link>
          </div>
        )}

        <div
          className={`relative flex md:hidden ${token !== null && user?.accountType !== "Instructor" ? "pl-1" : ""}`}
        >
          <button
            type="button"
            aria-label="Open menu"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-richblack-25 hover:bg-richblack-800"
            onClick={shownav}
          >
            <GiHamburgerMenu className="h-6 w-6" />
          </button>
          <div
            ref={overlay}
            className="fixed left-0 right-0 top-0 z-30 hidden h-[100vh] w-[100vw] overflow-y-hidden bg-black/50"
            onClick={shownav}
          />
          <div ref={menuRef} className="mobNav z-50">
            <nav className="absolute -left-[80px] -top-7 flex w-[220px] flex-col items-center rounded-2xl border border-richblack-700 bg-richblack-800 py-2 shadow-xl">
              {token == null && (
                <Link to="/login" onClick={() => dispatch(setProgress(100))}>
                  <button
                    type="button"
                    onClick={shownav}
                    className="mt-4 rounded-full border-2 border-richblack-25 px-6 py-2 text-[15px] font-semibold text-richblack-5 transition hover:bg-richblack-25 hover:text-richblack-900"
                  >
                    Login
                  </button>
                </Link>
              )}
              {token == null && (
                <Link to="/signup" onClick={() => dispatch(setProgress(100))}>
                  <button
                    type="button"
                    onClick={shownav}
                    className="mt-3 rounded-full bg-yellow-50 px-6 py-2 text-[15px] font-semibold text-richblack-900 transition hover:bg-yellow-100"
                  >
                    Sign up
                  </button>
                </Link>
              )}
              {token != null && (
                <div className="mt-2">
                  <p className="mb-2 text-center text-sm font-medium text-richblack-200">Account</p>
                  <ProfileDropDown />
                </div>
              )}
              <div className="my-4 h-px w-[88%] bg-richblack-600" />
              <p className="text-base font-semibold text-richblack-5">Courses</p>
              <div className="flex w-full flex-col items-stretch px-3">
                {!sublinks?.length ? (
                  <p className="p-2 text-sm text-richblack-300">No categories available.</p>
                ) : (
                  sublinks.map((element, index) => (
                    <Link
                      to={`/catalog/${element?.name}`}
                      key={index}
                      onClick={() => {
                        dispatch(setProgress(30));
                        shownav();
                      }}
                      className="rounded-lg p-2 text-sm text-richblack-25 hover:bg-richblack-700"
                    >
                      {element?.name}
                    </Link>
                  ))
                )}
              </div>
              <div className="my-4 h-px w-[88%] bg-richblack-600" />
              <Link
                to="/about"
                onClick={() => {
                  dispatch(setProgress(100));
                  shownav();
                }}
                className="p-2 text-sm text-richblack-25 hover:bg-richblack-700"
              >
                About
              </Link>
              <Link
                to="/contact"
                onClick={() => {
                  dispatch(setProgress(100));
                  shownav();
                }}
                className="p-2 text-sm text-richblack-25 hover:bg-richblack-700"
              >
                Contact
              </Link>
            </nav>
          </div>
        </div>

        <nav className="min-w-0 flex-1 md:flex md:justify-center">
          <ul className="hidden items-center gap-5 md:flex md:flex-row md:gap-x-6 lg:gap-x-8">
            {NavbarLinks?.map((element, index) => (
              <li key={index} className="shrink-0">
                {element.title === "Catalog" ? (
                  <div
                    className="relative flex cursor-pointer items-center gap-1"
                    onMouseEnter={() => setCatalogMenuOpen(true)}
                    onMouseLeave={() => setCatalogMenuOpen(false)}
                  >
                    <p className="text-richblack-25">{element.title}</p>
                    <svg
                      width="20"
                      height="18"
                      viewBox="0 0 24 24"
                      className="text-richblack-25"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M4.293 8.293a1 1 0 011.414 0L12 14.586l6.293-6.293a1 1 0 111.414 1.414l-7 7a1 1 0 01-1.414 0l-7-7a1 1 0 010-1.414z"
                        fill="currentColor"
                      />
                    </svg>

                    <div
                      className={`absolute left-1/2 top-full z-[1000] flex w-[240px] -translate-x-1/2 pt-3 transition duration-150 lg:w-[300px] ${
                        catalogMenuOpen
                          ? "visible translate-y-0 opacity-100"
                          : "invisible pointer-events-none translate-y-2 opacity-0"
                      }`}
                    >
                      <div className="relative flex w-full flex-col rounded-xl border border-richblack-700 bg-richblack-800 p-2 text-richblack-25 shadow-xl">
                      <div className="absolute left-1/2 top-0 z-[-1] h-4 w-4 -translate-y-1/2 translate-x-[-30%] rotate-45 border-l border-t border-richblack-700 bg-richblack-800" />
                      {!sublinks?.length ? (
                        <p className="rounded-lg py-4 pl-4 text-sm text-richblack-300">No categories available.</p>
                      ) : (
                        sublinks.map((el, idx) => (
                          <Link
                            to={`/catalog/${el?.name}`}
                            key={idx}
                            className="rounded-lg py-3 pl-4 hover:bg-richblack-700"
                            onMouseDown={() => setCatalogMenuOpen(false)}
                            onClick={() => dispatch(setProgress(30))}
                          >
                            <p className="font-medium text-richblack-5">{el?.name}</p>
                          </Link>
                        ))
                      )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link to={element?.path} onClick={() => dispatch(setProgress(100))}>
                    <p className={`hidden md:block ${matchRoutes(element?.path) ? linkActive : linkIdle}`}>
                      {element.title}
                    </p>
                  </Link>
                )}
              </li>
            ))}
            <li className="shrink-0">
              <form onSubmit={handelSearch} className="relative flex items-center">
                <input
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  id="searchinput"
                  type="text"
                  placeholder="Search"
                  className="w-28 rounded-full border border-richblack-600 bg-richblack-800 py-1.5 pl-3 pr-10 text-[15px] text-richblack-5 placeholder-richblack-400 focus:border-yellow-50 focus:outline-none focus:ring-2 focus:ring-yellow-50/25 md:w-36 lg:w-44"
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-richblack-25 hover:bg-richblack-700"
                  aria-label="Search"
                >
                  <HiSearch size={18} />
                </button>
              </form>
            </li>
          </ul>
        </nav>

        <div className="hidden shrink-0 items-center gap-3 md:flex md:gap-4">
          {user && user?.accountType !== "Instructor" && (
            <Link to="/dashboard/cart" className="relative inline-flex p-1" onClick={() => dispatch(setProgress(100))}>
              <TiShoppingCart className={`${iconFill} h-7 w-7 hover:fill-richblack-5`} />
              {totalItems > 0 && (
                <span
                  className={`absolute -right-0.5 -top-0.5 rounded-full px-1 py-0.5 text-[10px] font-bold leading-none shadow-sm ${cartBadge}`}
                >
                  {totalItems}
                </span>
              )}
            </Link>
          )}
          {token == null && (
            <Link to="/login" onClick={() => dispatch(setProgress(100))}>
              <span className="text-sm font-semibold text-richblack-25 hover:text-richblack-5 hover:underline">
                Login
              </span>
            </Link>
          )}
          {token == null && (
            <Link to="/signup" onClick={() => dispatch(setProgress(100))}>
              <button
                type="button"
                className="rounded-full bg-yellow-50 px-5 py-2 text-sm font-semibold text-richblack-900 shadow-sm transition hover:bg-yellow-100"
              >
                Sign up
              </button>
            </Link>
          )}
          {token !== null && (
            <div className="flex items-center pt-0.5">
              <ProfileDropDown />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NavBar;
